/**
 * Form Utilities
 * Shared utilities for form components following frontend best practices
 */

import React, { useState, useCallback } from 'react';

// ============================================================================
// Ref Management Utilities
// ============================================================================

/**
 * Merges multiple refs into a single ref callback
 * Following frontend rules for proper ref forwarding
 */
export function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T) =>
    refs.forEach((r) => {
      if (!r) return;
      if (typeof r === 'function') r(value);
      else (r as React.MutableRefObject<T | null>).current = value;
    });
}

/**
 * Composes multiple event handlers into a single handler
 * Ensures all handlers are called in sequence
 */
export function composeHandlers<T extends React.SyntheticEvent>(
  ...handlers: Array<((e: T) => void) | undefined>
) {
  return (e: T) => handlers.forEach((h) => h?.(e));
}

// ============================================================================
// Controllable State Hook
// ============================================================================

export interface UseControllableStateOptions<T> {
  value?: T | undefined;
  defaultValue?: T | undefined;
  onChange?: ((value: T) => void) | undefined;
}

/**
 * Hook for managing controlled/uncontrolled state pattern
 * Following React best practices for component state management
 */
export function useControllableState<T>({
  value,
  defaultValue,
  onChange,
}: UseControllableStateOptions<T>) {
  const [internalValue, setInternalValue] = useState<T | undefined>(
    defaultValue,
  );
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  const setValue = useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    },
    [isControlled, onChange],
  );

  return [currentValue, setValue] as const;
}

// ============================================================================
// Form State Utilities
// ============================================================================

/**
 * Builds aria-describedby string from array of IDs
 * Filters out null/undefined values
 */
export function buildAriaDescribedBy(
  ids: Array<string | null | undefined>,
): string | undefined {
  const validIds = ids.filter(Boolean);
  return validIds.length > 0 ? validIds.join(' ') : undefined;
}

/**
 * Generates stable unique IDs for form components
 * Uses React's useId for SSR safety
 */
export function useFormIds(name: string, customId?: string) {
  const autoId = React.useId();
  const baseId = customId || `${name}-${autoId}`;

  return {
    inputId: baseId,
    labelId: `${baseId}-label`,
    errorId: `${baseId}-error`,
    helperId: `${baseId}-helper`,
    listboxId: `${baseId}-listbox`,
  };
}

// ============================================================================
// Performance Utilities
// ============================================================================

/**
 * Creates a memoized index map for O(1) lookups
 * Replaces O(n²) indexOf operations in render loops
 */
export function useMemoizedIndexMap<T>(
  items: T[],
  keyExtractor: (item: T) => string | number,
) {
  return React.useMemo(() => {
    const map = new Map<string | number, number>();
    items.forEach((item, index) => {
      map.set(keyExtractor(item), index);
    });
    return map;
  }, [items, keyExtractor]);
}

/**
 * Flattens grouped options for keyboard navigation
 * Memoized to avoid recalculation on every render
 */
export function useFlattenedOptions<T>(groupedOptions: Record<string, T[]>) {
  return React.useMemo(() => {
    return Object.values(groupedOptions).flat();
  }, [groupedOptions]);
}

// ============================================================================
// Event Utilities
// ============================================================================

/**
 * Prevents event propagation and default behavior
 * Useful for clear buttons and nested interactive elements
 */
export function stopPropagation(e: React.MouseEvent | React.KeyboardEvent) {
  e.stopPropagation();
  e.preventDefault();
}

/**
 * Handles keyboard events for custom interactive elements
 * Supports Enter and Space key activation
 */
export function handleKeyboardActivation(
  e: React.KeyboardEvent,
  callback: () => void,
) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    callback();
  }
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if a value is a non-empty string
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Type guard to check if a value is a non-empty array
 */
export function isNonEmptyArray<T>(value: unknown): value is T[] {
  return Array.isArray(value) && value.length > 0;
}
